import React, { useState } from 'react';
import apptracker from '@utils/sensor/apptracker';
import { PROMPT_ERROR } from '@src/utils/sensor/track';
import { TIMELINE_SUMMARY_QUESTION } from '@components/prompt/utils';

const JSON_DELIMITER = '==END_JSON==';
const BASE_URL = `${window.__POWERED_BY_QIANKUN__ ? BUILD_VARS.PROMPT_AI_QUERY_DOMAIN : ''}/v1.0`;

type ErrorParams = {
  title?: string;
  description?: string;
};

export interface PromptQuestionRequestBody {
  query: string;
  product: string;
}

export interface PromptTimelineSummaryRequestBody {
  timelineEventQuery: object;
  userFilterNameList: string[];
}

function useGetStreamResponse(apiPath: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasStreamingEnded, setHasStreamingEnded] = useState(false);
  const [botData, setBotData] = useState([]);
  const [error, setError] = useState<{ title?: string; description?: string }>({});
  const [queryStartTime, setQueryStartTime] = useState(0);

  // reset state when a new question is asked by the user
  const prepStateForNewChat = () => {
    setIsLoading(true);
    setHasStreamingEnded(false);
    setError({});
    setBotData([]);
    setQueryStartTime(Date.now());
  };

  const handleError = (
    requestBody: PromptQuestionRequestBody | PromptTimelineSummaryRequestBody,
    { title = 'Something went wrong', description = 'Please try again' }: ErrorParams = {},
  ) => {
    setError({
      title,
      description,
    });
    setIsLoading(false);

    // track error in app insights
    if ('query' in requestBody) {
      apptracker(PROMPT_ERROR, {
        error_question: requestBody.query,
      });
    } else if ('timelineEventQuery' in requestBody) {
      apptracker(PROMPT_ERROR, {
        error_question: TIMELINE_SUMMARY_QUESTION,
      });
    }
  };

  // Request the LLM service and process the stream response
  const getStream = async (
    requestBody: PromptQuestionRequestBody | PromptTimelineSummaryRequestBody,
    cancelController?: AbortController,
  ) => {
    let res;
    let remainingData = '';

    try {
      prepStateForNewChat();
      try {
        res = await fetch(`${BASE_URL}/${apiPath}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
          credentials: 'include', // since the IFS vip is cross domain
          signal: cancelController?.signal,
        });
      } catch (error) {
        // Error: when there is some network error
        handleError(requestBody);
      }

      if (!res?.ok) {
        // Error: when the response from IF Server is not a 2xx
        handleError(requestBody);
      }

      // possible simpler alternative, not using right now because of possible browser support issues
      // res.body?.
      //   pipeThrough(new TextDecoderStream())
      //   .pipeTo(appendChildWritableStream());

      if (!res.body || !res.body.getReader) {
        // Error: When the response from IF Server is not in the right format
        handleError(requestBody);
        // todo-cp
        throw new Error('ReadableStream not supported');
      }

      // reads the stream of data
      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          // gets the value in the form of Uint8Array
          const { done, value } = await reader.read();

          // todo-cp: ensure that we get a done trigger for the loop to break
          if (done) {
            setIsLoading(false);
            setHasStreamingEnded(true);
            break;
          }

          if (value) {
            // convert Uint8Array to string
            const chunkString = decoder.decode(value);

            /**
             * the chunks contain partial json sometimes.
             * this logic is to concatenate adjacent chunks and try to get fully formed jsons and parse them
             */
            const combinedData = remainingData + chunkString;
            const lines = combinedData.split(JSON_DELIMITER);

            for (let i = 0; i < lines.length - 1; i++) {
              const line = lines[i].trim();
              try {
                const jsonData = JSON.parse(line);
                // if valid json, allow it as valid response to be shown on the chat
                setBotData(jsonData?.data);
              } catch {
                // Error: json pase error for once chunk of data, do nothing
              }
            }

            // store the last chunk of incomplete json, if any
            remainingData = lines[lines.length - 1];
          }
        } catch (error) {
          // this will mostly be reached when there is some issue while streaming the response
          handleError(requestBody);
          // https://conviva.atlassian.net/browse/DEEP-65
          break;
        }
      }
    } catch (error) {
      handleError(requestBody);
    }
  };

  return { isLoading, hasStreamingEnded, error, botData, getStream, queryStartTime };
}

export default useGetStreamResponse;
